import React, { useMemo } from "react";
import {
  useBranchesByCompany,
  useBranchHierarchy,
} from "../../api/queries/organizationQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];

/** Order branches as a tree (parent → children) with their depth for indenting. */
export const toBranchTree = (branches) => {
  const byParent = new Map();
  branches.forEach((b) => {
    const key = branches.some((p) => p.id === b.parentId) ? b.parentId : "root";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(b);
  });
  const out = [];
  const walk = (key, depth) =>
    (byParent.get(key) || [])
      .sort((a, b) => (a.branchName || "").localeCompare(b.branchName || ""))
      .forEach((b) => {
        out.push({ ...b, depth });
        walk(b.id, depth + 1);
      });
  walk("root", 0);
  return out;
};

/**
 * Branch dropdown shown as a tree. Company-wide roles (COMPANY scope) see the
 * company's branches; others see their own branch and its sub-branches.
 * emptyLabel: text for the "" option (e.g. "All branches" or "Company level").
 */
const BranchSelect = ({
  companyId,
  value,
  onChange,
  emptyLabel = "All branches",
  allowEmpty = true,
  id,
  disabled,
  required,
}) => {
  const user = useCurrentUser();
  const wide = usePermissions().anyCompanyWide;
  const byCompany = useBranchesByCompany(
    wide ? companyId || user.companyId : null,
  );
  const hierarchy = useBranchHierarchy(wide ? null : user.branchId);
  const branches = (wide ? byCompany.data : hierarchy.data) || EMPTY;
  const tree = useMemo(() => toBranchTree(branches), [branches]);
  // Branches belong to one company: with "All companies" there is nothing to list
  const noCompany = wide && !(companyId || user.companyId);

  return (
    <select
      id={id}
      className="form-select"
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      required={required}
      title={
        noCompany
          ? "Pick a company at the top right to filter by branch"
          : undefined
      }
    >
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {!allowEmpty && <option value="">Select branch</option>}
      {tree.map((b) => (
        <option key={b.id} value={b.id}>
          {"   ".repeat(b.depth)}
          {b.depth > 0 ? "↳ " : ""}
          {b.branchName || b.name}
        </option>
      ))}
    </select>
  );
};

export default BranchSelect;
